/**
 * DepotUploader - Upload load profile CSV for depot cost analysis
 *
 * Simple drag-and-drop CSV upload that POSTs to /upload-load-profile
 * and returns parsed load data via onComplete callback.
 */

import React, { useState, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Alert,
    Typography,
    Paper,
    alpha,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useAuth, useResponsive, useThemeMode, API_BASE_URL, getAuthHeaders, vortxColors } from '@vortx/shared-ui';

/**
 * @param {Object} props
 * @param {Function} props.onComplete - Called with { load_kw, interval_minutes, data_points, peak_kw, total_kwh, avg_daily_kwh }
 * @param {Function} [props.onError] - Called with error message string
 */
export default function DepotUploader({ onComplete, onError }) {
    const { currentUser } = useAuth();
    const { isMobile } = useResponsive();
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';

    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const droppedFile = e.dataTransfer?.files?.[0];
        if (droppedFile && droppedFile.name.toLowerCase().endsWith('.csv')) {
            setFile(droppedFile);
            setError(null);
        } else {
            setError('Please upload a CSV file');
        }
    }, []);

    const handleFileSelect = useCallback((e) => {
        const selected = e.target.files?.[0];
        if (selected) {
            setFile(selected);
            setError(null);
        }
    }, []);

    const handleSubmit = async () => {
        if (!file) { setError('Please select a CSV file'); return; }

        setSubmitting(true);
        setError(null);

        try {
            const headers = await getAuthHeaders(currentUser);
            delete headers['Content-Type'];

            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(
                `${API_BASE_URL}/api/flux_ev/v1/ev/depot/upload-load-profile`,
                { method: 'POST', headers, body: formData },
            );

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Upload failed');
            }

            onComplete(data);
        } catch (err) {
            const msg = err.message || 'Upload failed';
            setError(msg);
            if (onError) onError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Drag-and-drop file area */}
            <Paper
                variant="outlined"
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                    p: isMobile ? 3 : 4,
                    mb: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: dragActive
                        ? vortxColors.teal
                        : file
                            ? alpha(vortxColors.teal, 0.5)
                            : 'divider',
                    bgcolor: dragActive
                        ? alpha(vortxColors.teal, 0.04)
                        : file
                            ? alpha(vortxColors.teal, 0.02)
                            : 'transparent',
                    transition: 'border-color 0.2s, background-color 0.2s',
                    '&:hover': {
                        borderColor: vortxColors.teal,
                        bgcolor: alpha(vortxColors.teal, 0.04),
                    },
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                />
                {file ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <FileIcon sx={{ color: vortxColors.teal }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: isRetro ? '#f4f1ec' : 'text.primary' }}>
                            {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isRetro ? '#888' : 'text.secondary' }}>
                            ({(file.size / 1024).toFixed(1)} KB)
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <UploadIcon sx={{ fontSize: 40, color: isRetro ? '#5fd4c4' : vortxColors.stone, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: isRetro ? '#f4f1ec' : 'text.primary' }}>
                            Drag & drop a load profile CSV here
                        </Typography>
                        <Typography variant="caption" sx={{ color: isRetro ? '#888' : 'text.secondary' }}>
                            or click to browse — 8760 hourly or 35040 quarter-hourly kW values
                        </Typography>
                    </>
                )}
            </Paper>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || !file}
                    fullWidth={isMobile}
                    startIcon={
                        submitting
                            ? <CircularProgress size={18} color="inherit" />
                            : <UploadIcon />
                    }
                    sx={{
                        bgcolor: vortxColors.teal,
                        '&:hover': { bgcolor: vortxColors.darkGreen },
                        minWidth: 180,
                    }}
                >
                    {submitting ? 'Uploading...' : 'Upload Load Profile'}
                </Button>
            </Box>
        </Box>
    );
}
